/**
 * The Metrics overhaul's three reads (Akash, 2026-10-22), as the console
 * holds them:
 *
 *  - `GET /sites/{site}/metrics/cleaning` -- Summary and Cleaning coverage
 *    (`backend/api_server/cleaning_metrics.py`);
 *  - `GET /sites/{site}/metrics/lifts` -- Lifts & doors (built separately;
 *    an api-server without it answers 404, which is `null` here and a calm
 *    "starts once this is recorded" on screen);
 *  - `GET/PUT /sites/{site}/settings/metrics` -- the Site settings page.
 *
 * Every adapter is defensive: a missing or malformed field becomes its
 * "not reported" value (null / empty), never a made-up zero.
 */

export type CleanType = 'vacuum' | 'sweep' | 'dust_mop' | 'scrub';
export const CLEAN_TYPES: readonly CleanType[] = ['vacuum', 'sweep', 'dust_mop', 'scrub'];
export const CLEAN_TYPE_LABEL: Record<string, string> = {
  vacuum: 'Vacuum',
  sweep: 'Sweep',
  dust_mop: 'Dust-mop',
  scrub: 'Scrub',
};
/** Fixed order, never cycled: a type keeps its colour whatever else is shown. */
export const CLEAN_TYPE_COLOR: Record<string, string> = {
  vacuum: 'var(--nx-series-1)',
  sweep: 'var(--nx-series-2)',
  dust_mop: 'var(--nx-series-3)',
  scrub: 'var(--nx-series-4)',
};

/** Older api-servers and older cleans: `clean` with no mode is a vacuum,
 *  `wet_scrub` is a scrub. */
export function cleanTypeOf(type: string): string {
  if (type === 'clean') return 'vacuum';
  if (type === 'wet_scrub') return 'scrub';
  return type;
}

/** A fire alarm or fire drill stopped the clean (D5, 2026-10-23). Left
 *  out of "finished %" on both sides of the fraction. */
export const BUILDING_EMERGENCY_REASON = 'building_emergency';
/** Used only when the api-server sends a reason without its label. */
export const REASON_LABEL_FALLBACK: Record<string, string> = {
  [BUILDING_EMERGENCY_REASON]: 'Stopped for a building emergency (alarm or drill)',
};

export type ZoneGroup = 'foh' | 'boh';
export const ZONE_GROUP_LABEL: Record<ZoneGroup, string> = {
  foh: 'Front of house',
  boh: 'Back of house',
};

export interface ServiceSummary {
  service: string;
  label: string;
  robots: number;
}

export interface GroupCoverage {
  group: ZoneGroup;
  label: string;
  targetDays: number;
  zones: number;
  onTarget: number;
  onTargetPct: number | null;
  overdueCount: number;
  overdueZones: string[];
}

export interface OverdueZone {
  zone: string;
  level: string | null;
  group: ZoneGroup;
  targetDays: number;
  lastCleanedMs: number | null;
  overdueByMs: number | null;
  lastAttemptMs: number | null;
  lastAttemptState: string | null;
  lastAttemptReason: string | null;
  lastAttemptReasonLabel: string | null;
}

export type ZoneDayState = 'cleaned' | 'failed' | null;

export interface CleaningMetrics {
  windowStartMs: number;
  windowEndMs: number;
  timezone: string | null;
  /** The site's "Count metrics from" go-live date, as the start of that
   *  day in site time; null when not set (or an older api-server). Every
   *  figure ignores work before it. */
  metricsStartMs: number | null;
  services: ServiceSummary[];
  hoursSaved: number | null;
  moneySaved: number | null;
  cleansWithCleaningTime: number;
  cleansWithoutCleaningTime: number;
  areaM2: number | null;
  cleansWithArea: number;
  cleansStarted: number;
  cleansCompleted: number;
  cleansFailed: number;
  cleansCancelled: number;
  /** Cleans a building emergency (fire alarm or drill) stopped. They are
   *  not in cleansStarted/Failed/Cancelled nor in finishedPct (D5). */
  cleansStoppedForEmergency: number;
  finishedPct: number | null;
  labour: { rate: number | null; currency: string | null };
  hoursByDay: { date: string; byType: Record<string, number> }[];
  typeMix: { type: string; hours: number; sharePct: number | null; cleans: number }[];
  reasons: { reason: string; label: string; count: number }[];
  attention: {
    overdueZones: number;
    fohOnTargetPct: number | null;
    liftBlockedCleans: number;
    screenLockedStops: number;
    routineChecksLate: { late: number; total: number } | null;
  };
  coverage: {
    asOfMs: number;
    zonesTotal: number;
    zonesOnTarget: number;
    groups: GroupCoverage[];
    overdue: OverdueZone[];
  };
  grid: {
    dates: string[];
    endsToday: boolean;
    floors: { level: string; cleans: number[] }[];
    zones: {
      zone: string;
      level: string | null;
      group: ZoneGroup;
      days: ZoneDayState[];
      overdue: boolean;
    }[];
  };
  areaByFloor: { level: string; areaM2: number; cleans: number }[];
  compare: {
    hoursSaved: number | null;
    moneySaved: number | null;
    areaM2: number | null;
    cleansCompleted: number;
    cleansWithCleaningTime: number;
  } | null;
  registryDegraded: boolean;
}

type Obj = Record<string, unknown>;

function obj(v: unknown): Obj {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {};
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function int(v: unknown): number {
  return num(v) ?? 0;
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}
function isoDate(v: unknown): string | null {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}
function group(v: unknown): ZoneGroup {
  return v === 'boh' ? 'boh' : 'foh';
}

export function adaptCleaningMetrics(raw: unknown): CleaningMetrics {
  const o = obj(raw);
  const att = obj(o.attention);
  const cov = obj(o.coverage);
  const grid = obj(o.grid);
  const cmp = o.compare == null ? null : obj(o.compare);
  const late = att.routineChecksLate == null ? null : obj(att.routineChecksLate);
  return {
    windowStartMs: int(o.window_start_ms),
    windowEndMs: int(o.window_end_ms),
    timezone: str(o.timezone),
    metricsStartMs: num(o.metricsStartMs),
    services: arr(o.services).map((s) => {
      const x = obj(s);
      return {
        service: String(x.service ?? ''),
        label: String(x.label ?? ''),
        robots: int(x.robots),
      };
    }),
    hoursSaved: num(o.hoursSaved),
    moneySaved: num(o.moneySaved),
    cleansWithCleaningTime: int(o.cleansWithCleaningTime),
    cleansWithoutCleaningTime: int(o.cleansWithoutCleaningTime),
    areaM2: num(o.areaM2),
    cleansWithArea: int(o.cleansWithArea),
    cleansStarted: int(o.cleansStarted),
    cleansCompleted: int(o.cleansCompleted),
    cleansFailed: int(o.cleansFailed),
    cleansCancelled: int(o.cleansCancelled),
    cleansStoppedForEmergency:
      num(o.cleansStoppedForEmergency) ??
      arr(o.reasons)
        .map(obj)
        .filter((x) => x.reason === BUILDING_EMERGENCY_REASON)
        .reduce((n, x) => n + int(x.count), 0),
    finishedPct: num(o.finishedPct),
    labour: { rate: num(obj(o.labour).rate), currency: str(obj(o.labour).currency) },
    hoursByDay: arr(o.hoursByDay).map((d) => {
      const x = obj(d);
      const byType: Record<string, number> = {};
      for (const [k, v] of Object.entries(obj(x.byType))) {
        const n = num(v);
        if (n !== null) {
          const t = cleanTypeOf(k);
          byType[t] = (byType[t] ?? 0) + n;
        }
      }
      return { date: String(x.date ?? ''), byType };
    }),
    typeMix: arr(o.typeMix).map((t) => {
      const x = obj(t);
      return {
        type: cleanTypeOf(String(x.type ?? '')),
        hours: int(x.hours),
        sharePct: num(x.sharePct),
        cleans: int(x.cleans),
      };
    }),
    reasons: arr(o.reasons).map((r) => {
      const x = obj(r);
      const reason = String(x.reason ?? 'other');
      return {
        reason,
        label: str(x.label) ?? REASON_LABEL_FALLBACK[reason] ?? 'Other',
        count: int(x.count),
      };
    }),
    attention: {
      overdueZones: int(att.overdueZones),
      fohOnTargetPct: num(att.fohOnTargetPct),
      liftBlockedCleans: int(att.liftBlockedCleans),
      screenLockedStops: int(att.screenLockedStops),
      routineChecksLate: late ? { late: int(late.late), total: int(late.total) } : null,
    },
    coverage: {
      asOfMs: int(cov.asOfMs),
      zonesTotal: int(cov.zonesTotal),
      zonesOnTarget: int(cov.zonesOnTarget),
      groups: arr(cov.groups).map((g) => {
        const x = obj(g);
        return {
          group: group(x.group),
          label: String(x.label ?? ZONE_GROUP_LABEL[group(x.group)]),
          targetDays: int(x.targetDays),
          zones: int(x.zones),
          onTarget: int(x.onTarget),
          onTargetPct: num(x.onTargetPct),
          overdueCount: int(x.overdueCount),
          overdueZones: arr(x.overdueZones).map(String),
        };
      }),
      overdue: arr(cov.overdue).map((z) => {
        const x = obj(z);
        return {
          zone: String(x.zone ?? ''),
          level: str(x.level),
          group: group(x.group),
          targetDays: int(x.targetDays),
          lastCleanedMs: num(x.lastCleanedMs),
          overdueByMs: num(x.overdueByMs),
          lastAttemptMs: num(x.lastAttemptMs),
          lastAttemptState: str(x.lastAttemptState),
          lastAttemptReason: str(x.lastAttemptReason),
          lastAttemptReasonLabel:
            str(x.lastAttemptReasonLabel) ??
            REASON_LABEL_FALLBACK[str(x.lastAttemptReason) ?? ''] ??
            null,
        };
      }),
    },
    grid: {
      dates: arr(grid.dates).map(String),
      endsToday: grid.endsToday === true,
      floors: arr(grid.floors).map((f) => {
        const x = obj(f);
        return { level: String(x.level ?? ''), cleans: arr(x.cleans).map(int) };
      }),
      zones: arr(grid.zones).map((z) => {
        const x = obj(z);
        return {
          zone: String(x.zone ?? ''),
          level: str(x.level),
          group: group(x.group),
          days: arr(x.days).map((d) => (d === 'cleaned' || d === 'failed' ? d : null)),
          overdue: x.overdue === true,
        };
      }),
    },
    areaByFloor: arr(o.areaByFloor).map((a) => {
      const x = obj(a);
      return { level: String(x.level ?? ''), areaM2: int(x.areaM2), cleans: int(x.cleans) };
    }),
    compare: cmp
      ? {
          hoursSaved: num(cmp.hoursSaved),
          moneySaved: num(cmp.moneySaved),
          areaM2: num(cmp.areaM2),
          cleansCompleted: int(cmp.cleansCompleted),
          cleansWithCleaningTime: int(cmp.cleansWithCleaningTime),
        }
      : null,
    registryDegraded: o.registryDegraded === true,
  };
}

// ---- Lifts & doors ----------------------------------------------------------

export interface LiftRow {
  lift: string;
  label: string;
  trips: number;
  medianWaitS: number | null;
  p90WaitS: number | null;
  gaveUp: number;
  estopReleases: number;
  avgRideS: number | null;
}

export interface DoorRow {
  door: string;
  label: string;
  passes: number;
  failed: number;
  medianOpenS: number | null;
}

export interface LiftMetrics {
  windowStartMs: number;
  windowEndMs: number;
  dataSinceMs: number | null;
  /** Where every lift and door figure starts: max(window start, recording
   *  start, go-live date). Null from an api-server that predates it, or
   *  when nothing has been recorded yet. */
  effectiveStartMs: number | null;
  trips: number;
  medianWaitS: number | null;
  p90WaitS: number | null;
  gaveUp: number;
  gaveUpRetriedFinished: number;
  timeInLiftsPct: number | null;
  doorFailures: number;
  waitByHour: { hour: number; byLift: Record<string, number | null> }[];
  byLift: LiftRow[];
  byDoor: DoorRow[];
}

/** The lifts contract; `null` when the body is not one (an empty answer). */
export function adaptLiftMetrics(raw: unknown): LiftMetrics | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Obj;
  if (!('trips' in o) && !('byLift' in o)) return null;
  return {
    windowStartMs: int(o.window_start_ms),
    windowEndMs: int(o.window_end_ms),
    dataSinceMs: num(o.dataSinceMs),
    effectiveStartMs: num(o.effectiveStartMs),
    trips: int(o.trips),
    medianWaitS: num(o.medianWaitS),
    p90WaitS: num(o.p90WaitS),
    gaveUp: int(o.gaveUp),
    gaveUpRetriedFinished: int(o.gaveUpRetriedFinished),
    timeInLiftsPct: num(o.timeInLiftsPct),
    doorFailures: int(o.doorFailures),
    waitByHour: arr(o.waitByHour)
      .map((h) => {
        const x = obj(h);
        const byLift: Record<string, number | null> = {};
        for (const [k, v] of Object.entries(obj(x.byLift))) byLift[k] = num(v);
        return { hour: int(x.hour), byLift };
      })
      .filter((h) => h.hour >= 0 && h.hour <= 23),
    byLift: arr(o.byLift).map((l) => {
      const x = obj(l);
      const lift = String(x.lift ?? '');
      return {
        lift,
        label: str(x.label) ?? lift,
        trips: int(x.trips),
        medianWaitS: num(x.medianWaitS),
        p90WaitS: num(x.p90WaitS),
        gaveUp: int(x.gaveUp),
        estopReleases: int(x.estopReleases),
        avgRideS: num(x.avgRideS),
      };
    }),
    byDoor: arr(o.byDoor).map((d) => {
      const x = obj(d);
      const door = String(x.door ?? '');
      return {
        door,
        label: str(x.label) ?? door,
        passes: int(x.passes),
        failed: int(x.failed),
        medianOpenS: num(x.medianOpenS),
      };
    }),
  };
}

/** True when nothing was recorded in the window at all. The api-server
 *  always lists the site's lifts (a roster), so a row with nothing on it
 *  is not a recording: only a trip, a give-up, an e-stop release, a door
 *  pass or a door failure is. */
export function liftMetricsEmpty(m: LiftMetrics | null): boolean {
  if (!m) return true;
  if (m.trips > 0 || m.gaveUp > 0 || m.doorFailures > 0) return false;
  if (m.byLift.some((l) => l.trips > 0 || l.gaveUp > 0 || l.estopReleases > 0)) return false;
  if (m.byDoor.some((d) => d.passes > 0 || d.failed > 0)) return false;
  return true;
}

/** Where the lift and door figures start, when that is later than the
 *  period's start (or unknown). `null` = they cover the whole period.
 *  `{ startMs: null }` = nothing has been recorded yet.
 *  `{ startMs, afterPeriod: true }` = they start at or after the period's
 *  end (recording began, or the go-live date falls, after it), so nothing
 *  in this period is counted.
 *
 *  The api-server clips effectiveStartMs to the window's end and sends
 *  dataSinceMs null when nothing is recorded, so dataSinceMs decides
 *  "not recorded yet", not effectiveStartMs. */
export function liftFiguresStart(
  m: LiftMetrics,
): { startMs: number | null; afterPeriod?: true } | null {
  if (m.dataSinceMs === null) return { startMs: null };
  const start = m.effectiveStartMs ?? m.dataSinceMs;
  if (start >= m.windowEndMs) return { startMs: start, afterPeriod: true };
  return start > m.windowStartMs ? { startMs: start } : null;
}

// ---- Site settings ----------------------------------------------------------

export interface SiteMetricSettings {
  labourRate: number | null;
  currency: string | null;
  fohTargetDays: number;
  bohTargetDays: number;
  zones: { zone: string; level: string | null; group: ZoneGroup }[];
  registryDegraded: boolean;
  currencies: string[];
  minTargetDays: number;
  maxTargetDays: number;
  canEdit: boolean;
  updatedAtMs: number | null;
  /** "Count metrics from" (go-live) date, `YYYY-MM-DD` in site time; null
   *  when not set. */
  metricsStartDate: string | null;
}

export interface SiteMetricSettingsUpdate {
  labourRate?: number | null;
  currency?: string | null;
  fohTargetDays?: number;
  bohTargetDays?: number;
  zones?: Record<string, ZoneGroup>;
  metricsStartDate?: string | null;
}

export function adaptSiteMetricSettings(raw: unknown): SiteMetricSettings {
  const o = obj(raw);
  const currencies = arr(o.currencies).map(String);
  return {
    labourRate: num(o.labourRate),
    currency: str(o.currency),
    fohTargetDays: num(o.fohTargetDays) ?? 2,
    bohTargetDays: num(o.bohTargetDays) ?? 2,
    zones: arr(o.zones).map((z) => {
      const x = obj(z);
      return { zone: String(x.zone ?? ''), level: str(x.level), group: group(x.group) };
    }),
    registryDegraded: o.registryDegraded === true,
    currencies: currencies.length ? currencies : ['SGD'],
    minTargetDays: num(o.minTargetDays) ?? 1,
    maxTargetDays: num(o.maxTargetDays) ?? 30,
    canEdit: o.canEdit === true,
    updatedAtMs: num(o.updatedAtMs),
    metricsStartDate: isoDate(o.metricsStartDate),
  };
}
